import React from 'react';
import { astroSvgs } from '../../static/astro';
import { planets_dictionary } from '../../static/planets_dictionary';
import { nodes_dn, nodes_up } from '../../static/variables_dictionary';
import {
  penta_comp,
  penta_dom,
  penta_em,
  penta_same,
  penta_same_gates,
} from '../utilsPenta';

const ARROW_RIGHT = `<svg width="26" height="25" viewBox="0 0 26 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M14.9837 24.0237C14.2051 24.8023 12.9435 24.8051 12.1615 24.0298L12.0362 23.9056C11.2494 23.1256 11.2466 21.8545 12.0301 21.0711L18.3952 14.7059H1.15234C0.600058 14.7059 0.152344 14.2582 0.152344 13.7059V11.3849C0.152344 10.8326 0.600059 10.3849 1.15234 10.3849H18.3952L12.0293 4.02945C11.246 3.2474 11.2468 1.97782 12.0312 1.19681L12.1614 1.06717C12.943 0.288998 14.2069 0.290364 14.9868 1.07022L25.0478 11.1312C25.8288 11.9123 25.8288 13.1786 25.0478 13.9596L14.9837 24.0237Z" fill="currentColor"/></svg>`;
const ARROW_LEFT = `<svg width="27" height="25" viewBox="0 0 27 25" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M11.6305 24.0237C12.4092 24.8023 13.6708 24.8051 14.4528 24.0298L14.578 23.9056C15.3649 23.1256 15.3676 21.8545 14.5842 21.0711L8.21902 14.7059H25.4619C26.0142 14.7059 26.4619 14.2582 26.4619 13.7059V11.3849C26.4619 10.8326 26.0142 10.3849 25.4619 10.3849H8.21902L14.5849 4.02945C15.3683 3.2474 15.3674 1.97782 14.583 1.19681L14.4528 1.06717C13.6713 0.288998 12.4073 0.290364 11.6275 1.07022L1.56647 11.1312C0.78542 11.9123 0.785419 13.1786 1.56647 13.9596L11.6305 24.0237Z" fill="currentColor"/></svg>`;

const extractSvgParts = (svgString, { keepAccent = false } = {}) => {
  if (!svgString || typeof svgString !== 'string') return null;
  const viewBox = svgString.match(/viewBox="([^"]+)"/i)?.[1] || '0 0 24 24';
  let inner = svgString
    .replace(/<svg[^>]*>/i, '')
    .replace(/<\/svg>/i, '');

  if (keepAccent) {
    inner = inner
      .replace(/\sfill="(black|#000(?:000)?)"/gi, ' fill="currentColor"')
      .replace(/\sstroke="(black|#000(?:000)?)"/gi, ' stroke="currentColor"');
  } else {
    inner = inner
      .replace(/\sfill="(?!none")[^"]*"/gi, ' fill="currentColor"')
      .replace(/\sstroke="(?!none")[^"]*"/gi, ' stroke="currentColor"');
  }

  inner = inner.trim();
  return inner ? { viewBox, inner } : null;
};

const ICONS = {};

const register = (name, svg, options) => {
  const parts = extractSvgParts(svg, options);
  if (parts) ICONS[name] = parts;
};

Object.entries(planets_dictionary).forEach(([name, data]) => {
  register(name, data.svg);
});

Object.entries(astroSvgs).forEach(([name, svg]) => {
  register(name, svg);
});

register('arrow-left', ARROW_LEFT);
register('arrow-right', ARROW_RIGHT);
register('arrow-up', nodes_up);
register('arrow-down', nodes_dn);

register('penta-comp', penta_comp, { keepAccent: true });
register('penta-em', penta_em, { keepAccent: true });
register('penta-same', penta_same, { keepAccent: true });
register('penta-same-gates', penta_same_gates, { keepAccent: true });
register('penta-dom', penta_dom, { keepAccent: true });

const SPRITE_HTML = `<defs>${Object.entries(ICONS)
  .map(([name, { viewBox, inner }]) => (
    `<symbol id="astro-${name}" viewBox="${viewBox}">${inner}</symbol>`
  ))
  .join('')}</defs>`;

export const hasAstroIcon = (name) => Boolean(name && ICONS[name]);

export const AstroSprite = React.memo(() => (
  <svg
    id="astro-sprite"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden="true"
    focusable="false"
    style={{
      position: 'absolute',
      width: 0,
      height: 0,
      overflow: 'hidden',
    }}
    dangerouslySetInnerHTML={{ __html: SPRITE_HTML }}
  />
));

export const AstroIcon = React.memo(({
  name,
  size = 20,
  width,
  height,
  color,
  className = '',
  style = {},
}) => {
  if (!hasAstroIcon(name)) return null;

  const w = width ?? size;
  const h = height ?? size;
  const href = `#astro-${name}`;
  const viewBox = ICONS[name].viewBox;
  const vb = viewBox.split(/[\s,]+/).map(Number);
  const vbW = vb[2] || 24;
  const vbH = vb[3] || 24;

  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      xmlnsXlink="http://www.w3.org/1999/xlink"
      width={w}
      height={h}
      viewBox={viewBox}
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
      style={{
        display: 'block',
        width: w,
        height: h,
        overflow: 'hidden',
        flexShrink: 0,
        color: color || 'currentColor',
        ...style,
      }}
    >
      <use href={href} xlinkHref={href} width={vbW} height={vbH} />
    </svg>
  );
});
